<script lang="ts" setup>
import type { KUICoreRegionSelectOption } from '~kui/components/KUI/Core/RegionSelectDialog/KUICoreRegionSelectDialog.vue';
import type { ComponentPublicInstance } from 'vue';

import { Button } from '~ds/components/ui/button';

import { useGeoRouting } from '~/composables/useGeoRouting';
import { GEO_FLAG_EMOJI } from '~/utils/geo';

/**
 * Mounts the geo routing prompts on every page: the region selector dialog
 * (first visit on the wrong region) and the "page may not be available"
 * confirmation dialog. All behaviour — including the mount sequence
 * (`startPrompts`) — lives in `useGeoRouting`; this component is presentation.
 */
const { t } = useI18n();
const {
  regions,
  selector,
  pendingSwitch,
  dismissSelector,
  selectRegion,
  confirmPendingSwitch,
  declinePendingSwitch,
  startPrompts,
} = useGeoRouting();

const selectorOpen = computed({
  get: () => selector.value.open,
  set: (value) => {
    if (value) {
      selector.value.open = true;
      return;
    }
    dismissSelector();
  },
});

const selectorRegions = computed<KUICoreRegionSelectOption[]>(() =>
  regions.value.map(region => ({
    geo: region.geo,
    label: region.label,
    flag: GEO_FLAG_EMOJI[region.geo],
    isCurrent: region.isCurrent,
  })),
);

const pendingRegionLabel = computed(() => pendingSwitch.value?.region.label ?? '');

const handleSelect = (option: KUICoreRegionSelectOption): void => {
  const region = regions.value.find(candidate => candidate.geo === option.geo);
  if (region) {
    selectRegion(region);
  }
};

// The auto-routing dialog opens unsolicited on load, so default focus must land
// on the safe "Stay" action, not the page-leaving "Continue" — otherwise a
// reflexive Enter loses the visitor's page. (Escape / click-outside already
// decline safely: the dialog is not prevent-close.) An explicit switch the
// visitor themselves started keeps the default focus on Continue.
const stayButton = ref<ComponentPublicInstance | null>(null);
const onConfirmOpenAutoFocus = (event: Event): void => {
  if (pendingSwitch.value?.source !== 'auto') {
    return;
  }
  event.preventDefault();
  const el: unknown = stayButton.value?.$el;
  if (el instanceof HTMLElement) {
    el.focus();
  }
};

// Never in SSR markup: crawlers get clean HTML and hydration stays flash-free.
onMounted(() => {
  startPrompts();
});
</script>

<template>
  <KUICoreRegionSelectDialog
    v-model="selectorOpen"
    :regions="selectorRegions"
    :title="t('unified.geoRegion.selector.title')"
    :description="t('unified.geoRegion.selector.description')"
    :current-label="t('unified.geoRegion.selector.current')"
    :dismissible="!selector.locked"
    @select="handleSelect"
  />

  <KUICoreDialog
    :model-value="!!pendingSwitch"
    :heading="t('unified.geoRegion.confirmDialog.title', { region: pendingRegionLabel })"
    :description="t('unified.geoRegion.confirmDialog.description', { region: pendingRegionLabel })"
    alignment="center"
    content-width="fit"
    has-overlay
    :show-close-button="false"
    data-testid="region-confirm-dialog"
    @open-auto-focus="onConfirmOpenAutoFocus"
    @update:model-value="declinePendingSwitch"
  >
    <div class="flex w-full flex-col gap-3">
      <Button
        data-testid="region-confirm-continue"
        size="sm"
        type="button"
        class="w-full"
        @click="confirmPendingSwitch"
      >
        {{ t('unified.geoRegion.confirmDialog.continue', { region: pendingRegionLabel }) }}
      </Button>
      <Button
        ref="stayButton"
        variant="outline"
        data-testid="region-confirm-stay"
        size="sm"
        type="button"
        class="w-full"
        @click="declinePendingSwitch"
      >
        {{ t('unified.geoRegion.confirmDialog.stay') }}
      </Button>
    </div>
  </KUICoreDialog>
</template>
